import "bootstrap-icons/font/bootstrap-icons.css";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getApplicationMetadata } from "../../services/Application";
import { addInputToStep, commitStep, getStep } from "../../services/Process";
import { useAuth } from "../../context/AuthContext";
import LoadingSpinner from "./LoadingSpinner";
import ConfirmPopup from "./ConfirmPopup";
import {
  operatorCanRunProcess,
  clientCanRunProcess,
  shippingCanRunProcess,
} from "./ReadOnly";
import config from "../../config";


import logger from "../../utils/logger";
/**
 * Panel obsługujący akcje przejścia w procesie zgłoszenia
 *
 * @component
 * @param {Boolean} embedded - Czy osadzony
 * @param {String} applicationMode - Tryb wyświetlania
 * @param {Function} validate - Funkcja walidacyjna
 * @param {Function} onNext - Funkcja wywoływana przy przejściu dalej
 * @returns {Component}
 */
function ProcessPanel({
  embedded = false,
  applicationMode = false,
  validate = null,
  onNext = null,
}) {
  let { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { checkPermission, accessToken, userName } = useAuth();
  const [stepDescription, setStepDescription] = useState();
  const [stepDestinations, setStepDestinations] = useState();
  const [selectedValue, setSelectedValue] = useState();
  const [displayName, setDisplayName] = useState();
  const [statusId, setStatusId] = useState();
  const [status, setStatus] = useState();
  const [statusFront, setStatusFront] = useState();
  const [needsReload, setNeedsReload] = useState(0);
  const [finalStep, setFinalStep] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const debug = config.runmode.debug;

  /**
   * Funkcja obsługująca przejście dalej
   *
   * @function
   * @returns
   */
  const runProcess = async () => {
    const valid = validate?.();
    if (!valid && validate != null) return;

    setInProgress(true);

    if (stepDestinations.length === 1) {
      await performCommit();
    } else if (stepDestinations.length > 1) {
      await performInputValueAndCommit();
    }
    onNext && (await onNext());
    setSelectedValue(null);
    setInProgress(false);

    if (applicationMode) {
      const params = new URLSearchParams(location.search || "");
      params.set("view", "metadata");
      params.delete("focus");
      const search = params.toString();
      navigate(
        `${location.pathname}${search ? `?${search}` : ""}${location.hash || ""}`,
        { replace: true }
      );
      return;
    }

    document.location.reload();
  };

  const handleNext = async () => {
    const selectedDestination = stepDestinations?.find(
      (sd) => sd.stepId === selectedValue
    );
    const isClientCancel =
      selectedDestination?.description === "Anuluj wniosek klienta" &&
      selectedDestination?.stepId === "E.2";

    if (isClientCancel) {
      setShowCancelConfirm(true);
      return;
    }

    await runProcess();
  };

  const performCommit = async () => {
    try {
      const result = await commitStep(id, userName, accessToken);
      setNeedsReload((prev) => prev + 1);
      if (debug) logger.log("commitStep - PP:", result);
    } catch (error) {
      logger.error("Error committing step:", error);
    }
  };

  const performInputValueAndCommit = async () => {
    try {
      const result = await addInputToStep(
        id,
        selectedValue,
        userName,
        accessToken
      );
      setNeedsReload((prev) => prev + 1);
      if (debug) logger.log("addInputToStep - PP:", result);
    } catch (error) {
      logger.error("Error adding input to step:", error);
    }
  };

  /**
   * Funkcja obsługująca wybranie akcji przejścia w procesie
   *
   * @funcion
   * @param {String} value - Wybrana wartość
   */
  const handleSelect = (value) => {
    setSelectedValue(value);
  };

  useEffect(() => {
    if (debug) logger.log("needsReload changed:", needsReload);
    const fetchData = async () => {
      try {
        const stepData = await getStep(id, accessToken);
        setStepDescription(stepData.Step.id + ": " + stepData.Step.description);
        setStepDestinations(stepData.Destinations);
        setFinalStep(stepData.Step.id.startsWith("E."));

        let tempMetadata = await getApplicationMetadata(id, accessToken);
        setStatusId(tempMetadata.status);
        setStatus(tempMetadata.statusNameInternal);
        setStatusFront(tempMetadata.statusName);
        setDisplayName(tempMetadata.displayName);
      } catch (error) {
        logger.error("Error fetching step data:", error);
      }
    };

    fetchData();
  }, [needsReload, id]);

  useEffect(() => {
    if (debug) logger.log("PP - uE - selectedValue: " + selectedValue);
  }, [selectedValue]);

  const canCancelClientApplication =
    (checkPermission("Zmiana przypisania sprawy") ||
      checkPermission("Zarządzanie użytkownikami")) &&
    [4, 11].includes(statusId);

  const filteredDestinations = stepDestinations
    ? stepDestinations.filter((sd) => {
        if (sd.description !== "Anuluj wniosek klienta") return true;
        return canCancelClientApplication;
      })
    : stepDestinations;

  if (
    (checkPermission("Obsługa wniosków") && operatorCanRunProcess(statusId)) ||
    (checkPermission("Obsługa wysyłki") && shippingCanRunProcess(statusId)) ||
    (!checkPermission("Obsługa wniosków") &&
      !checkPermission("Obsługa wysyłki") &&
      clientCanRunProcess(statusId)) ||
    canCancelClientApplication
  )
    return (
      <div
        className={
          embedded
            ? "d-flex flex-column " +
              (applicationMode ? "" : " pt-1 mt-1 border-top")
            : "d-flex flex-column pe-1 w19"
        }
      >
        {!embedded && (
          <div>
            <h3>OBSŁUGA WNIOSKU</h3>
            <div className="ms-auto very-small">
              {stepDescription ? (
                stepDescription.split(": ")[0]
              ) : (
                <LoadingSpinner />
              )}
            </div>
          </div>
        )}

        <div>
          {stepDescription && embedded && (
            <div className="w-100 d-flex flex-row align-items-center mb-1">
              <div>
                <i
                  className={`fs-1 bi bi-${
                    finalStep
                      ? "check-circle-fill text-success"
                      : stepDestinations.length === 1
                      ? "arrow-right-circle-fill text-danger"
                      : "question-circle-fill text-danger"
                  }`}
                />
              </div>

              <div className="ms-1">
                <strong>{stepDescription.split(": ")[1]}</strong>
                <br />
                <span className="text-black-50">
                  Krok: {stepDescription.split(": ")[0]}
                </span>
              </div>
            </div>
          )}

          {stepDestinations && !embedded && (
            <div className="form-label">
              <strong>{stepDescription.split(": ")[1]}</strong>
            </div>
          )}

          {filteredDestinations ? (
            filteredDestinations.map((sd, index) => (
              <div
                className="form-control rounded-3 w-100 p-1 mb-1 hand"
                key={index}
                onClick={() => handleSelect(sd.stepId)}
              >
                <div className="d-flex flex-row">
                  <input
                    type="radio"
                    className="form-check-input me-1"
                    style={{ cursor: "pointer" }}
                    name={"processOption" + index}
                    id={"processOption" + index}
                    checked={selectedValue === sd.stepId}
                    onChange={() => handleSelect(sd.stepId)}
                  />

                  <label
                    className="form-check-label"
                    style={{ cursor: "pointer" }}
                    htmlFor={"processOption" + index}
                  >
                    {sd.description}
                  </label>
                </div>
              </div>
            ))
          ) : (
            <LoadingSpinner />
          )}
        </div>
        {!finalStep && (
          <div className="d-flex flex-row">
            {selectedValue ? (
              inProgress ? (
                <button className="btn btn-primary ms-auto w11 h2" disabled>
                  <LoadingSpinner text="Przetwarzanie" />
                </button>
              ) : (
                <button
                  className="btn btn-primary ms-auto w11 h2"
                  onClick={handleNext}
                >
                  Dalej
                </button>
              )
            ) : (
              <button className="btn btn-primary ms-auto w11 h2" disabled>
                Dalej
              </button>
            )}
          </div>
        )}
        <ConfirmPopup
          show={showCancelConfirm}
          title="POTWIERDŹ"
          text="Zamierzasz anulować wniosek, który został przekazany do klienta. Czy potwierdzasz anulację?"
          confirmText="Tak, anuluj"
          cancelText="Nie"
          onConfirm={async () => {
            setShowCancelConfirm(false);
            await runProcess();
          }}
          onCancel={() => setShowCancelConfirm(false)}
        />
      </div>
    );
}

export default ProcessPanel;


